'use client';

import {
  SeoSpecialty,
  Branch,
  BusinessModel,
  SeoExam,
} from '@medprev-web-app/api-graphql-client';

import { CtaProcedureNotifyMeButton } from './buttons/cta-procedure-notify-me-button.component';
import { CtaProcedureMedprevOnlineScheduleButton } from './buttons/cta-procedure-medprevonline-schedule-button.component';
import { CtaProcedureAssistedPurchaseButton } from './buttons/cta-procedure-assisted-purchase.component';
import { CtaProcedureTelemedicineButton } from './buttons/cta-procedure-telemedicine.component';
import { CtaProcedureBranchesButton } from './buttons/cta-procedure-branches-button.component';

import { useWhatsappLinkWithAnalytics } from '@/hooks/use-whatsapp/use-whatsapp-link-with-analytics.hook';
import { phonePatientSac } from '@/utils/consts/sac.consts';
import { SearchType } from '@/types/most-search-type';
import { Place } from '@/types/google-maps.types';
import { getServiceChannelFlags } from '@/app/(main)/(pages)/_utils/business-model.util';
import { useWhatsappLinkSimple } from '@/hooks/use-whatsapp/use-whatsapp-link-simple.hook';
import { scheduleProcedureWhatsappMessage } from '@/utils/consts/cta-whatsapp-messages.consts';

export interface CtaSectionProps {
  place: Pick<Place, 'state' | 'city' | 'slug'>;
  procedure?: SeoSpecialty | SeoExam | null;
  rules?: BusinessModel | null;
  branches?: Array<Branch | null> | null;
  searchType: SearchType;
  originCta: string;
}

export default function CtaSection({
  rules,
  place,
  procedure,
  branches,
  searchType,
  originCta,
}: CtaSectionProps) {
  const {
    branchesLength,
    hasTelemedicine,
    hasAssistedPurchase,
    hasMedprevOnline,
    hasBranches,
    noServiceType,
  } = getServiceChannelFlags({
    businessModelByCity: rules,
    branchesByCity: branches,
  });

  const message
    = scheduleProcedureWhatsappMessage[searchType as keyof typeof scheduleProcedureWhatsappMessage]?.({
      procedureName: String(procedure?.name),
      city: place?.city,
      state: place?.state,
    }) ?? '';

  const assistedShopUrl = useWhatsappLinkWithAnalytics(phonePatientSac, message)?.url;

  const branchWhatsapp = branches?.[0]?.whatsapp || '';

  const singleBranchPhoneUrl = useWhatsappLinkSimple(
    branchWhatsapp,
    message,
  ).url;

  const branchPhoneUrl = branchWhatsapp ? singleBranchPhoneUrl : null;

  const buttons: React.ReactNode[] = [];

  if (hasMedprevOnline) {
    buttons.push(
      <CtaProcedureMedprevOnlineScheduleButton
        key="cta-procedure-medprev-online-schedule-button"
        procedureName={procedure?.name || ''}
        city={place?.city}
        state={place?.state}
        originCta={originCta}
        searchType={searchType}
      />,
    );
  }

  if (hasAssistedPurchase) {
    buttons.push(
      <CtaProcedureAssistedPurchaseButton
        key="cta-procedure-assisted-purchase-button"
        assistedShopUrl={assistedShopUrl}
        hasMedprevOnline={hasMedprevOnline}
        city={place?.city}
        state={place?.state}
        originCta={originCta}
        searchType={searchType}
      />,
    );
  }

  if (hasBranches) {
    const onlyBranches = !hasMedprevOnline && !hasAssistedPurchase;
    const combinedWithOthers
      = (hasMedprevOnline && !hasAssistedPurchase)
        || (!hasMedprevOnline && hasAssistedPurchase);

    if (onlyBranches || combinedWithOthers) {
      buttons.push(
        <CtaProcedureBranchesButton
          key="cta-procedure-branches-button"
          onlyBranches={onlyBranches}
          combinedWithOthers={combinedWithOthers}
          branchesLength={branchesLength}
          singleBranchPhoneUrl={branchPhoneUrl}
          branches={branches}
          procedure={procedure}
          place={place}
          searchType={searchType}
          originCta={originCta}
        />,
      );
    }
  }

  if (hasTelemedicine && !hasMedprevOnline) {
    const onlyTelemedicine = !hasBranches && !hasAssistedPurchase;
    const combinedWithOthers
      = (hasBranches && !hasAssistedPurchase)
        || (!hasBranches && hasAssistedPurchase);
    const telehealthUrl = procedure && 'telehealthUrl' in procedure
      ? (procedure as SeoSpecialty).telehealthUrl
      : null;

    buttons.push(
      <CtaProcedureTelemedicineButton
        key="cta-procedure-telemedicine-button"
        telehealthUrl={telehealthUrl}
        citySlug={place.slug}
        onlyTelemedicine={onlyTelemedicine}
        combinedWithOthers={combinedWithOthers}
        procedure={procedure}
        place={place}
        originCta={originCta}
      />,
    );
  }

  if (noServiceType) {
    buttons.push(
      <CtaProcedureNotifyMeButton
        key="cta-procedure-notify-me"
        procedure={procedure}
        place={place}
      />,
    );
  }

  return (
    <div className="w-full flex flex-col xl:flex-row gap-3">
      {buttons}
    </div>
  );
}
